<template>
  <template v-if="!fa">
    <i class="ak-icon" :class="classes"></i>
  </template>
  <template v-else>
    <ak-fa-icon :light="faLight" :type="type" :size="size" :color="color"></ak-fa-icon>
  </template>
</template>

<script>
import { computed, defineComponent } from 'vue'
import { sizes } from './Icon.config'
import faIcon from './FaIcon.vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-icon :size="'small'" :type="'fireball"></ak-icon>
   */
  name: 'AkIcon',
  components: {
    [faIcon.name]: faIcon,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Object}
     * @prop {Function}
     * @prop {String}
     * @description Controls the type of the Icon i.e "fireball"
     */
    type: {
      default: undefined,
      type: String,
    },

    /**
     * @type {Object}
     * @prop {Function}
     * @prop {String}
     * @description Controls the color of the Icon i.e "primary"
     */
    color: {
      default: undefined,
      type: String,
    },

    /**
     * @type {Object}
     * @prop {Function}
     * @prop {String}
     * @description Controls the size of the Icon i.e "large"
     */
    size: {
      validator: (v) => sizes.indexOf(v) >= 0,
      default: 'default',
      type: String,
    },

    /**
     * @type {Object}
     * @prop {Function}
     * @prop {String}
     * @description Controls if the icon spins or not
     */
    spin: {
      default: false,
      type: Boolean,
    },

    /**
     * @type {Boolean}
     * @description Controls if the icon is using a font awesome icon or not
     */
     fa: {
      default: false,
      type: Boolean,
    },

    /**
     * @type {Boolean}
     * @description Controls if the font awesome icon is light
     */
     faLight: {
      default: false,
      type: Boolean,
    },
  },

  /**
   * @param {Object} props - The properties of the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props) {
    const classes = computed(() => {
      const res = [`icon-${props.type}`]     

      /* istanbul ignore else */
      if (typeof props.size === 'string' && props.size.length) {
        res.push(`ak-icon--${props.size}`)
      }

      if (typeof props.spin === 'boolean' && props.spin === true) {
        res.push(`ak-icon--spin`)
      }

      if (typeof props.color === 'string' && props.color.length) {
        res.push(`color-${props.color}`)
      }

      return res
    })

    return {
      classes,
    }
  },
})
</script>
<style lang="scss">
.ak-icon {
  display: inline-block;
  line-height: 1;
  font-family: 'icons';
  speak: none;
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
  text-rendering: auto;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  vertical-align: middle;

  &--tiny {
    font-size: 70%;
  }

  &--small {
    font-size: 85%;
  }

  &--big {
    font-size: 130%;
  }

  &--large {
    font-size: 175%;
  }

  &--huge {
    font-size: 250%;
  }

  &--gigantic {
    font-size: 350%;
  }

  &--spin {
    animation: ak-icon-rotation 2s linear infinite;
  }
}

@keyframes ak-icon-rotation {
  100% {
    transform: rotate(360deg);
  }
}
</style>
